import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { pressable, type MotionSafeProps } from '@/lib/motion';

/*
 * Restyled to ScreenAtlas tokens (web/docs/ui-ux.md §5):
 * - `accent` is the one marquee-amber next action; `neutral` is the
 *   raised secondary — `surface-alt` + the inset highlight hairline
 *   (`shadow-highlight`, ui-ux.md §2 "Material"), one step lighter on hover; `outline` is a quiet bordered secondary for
 *   paired choices (Google / Apple); `ghost` for quiet actions;
 *   `destructive` for danger confirms.
 * - Press/hover motion comes from src/lib/motion.ts (pressable), never inline.
 * - The focus ring is the app-wide :focus-visible outline (index.css).
 */
const buttonVariants = cva(
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded text-headline transition-[background-color,border-color,color] duration-150 ease-out disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        accent: 'bg-accent text-on-accent shadow-highlight hover:bg-accent-hover',
        neutral: 'bg-surface-alt text-ink shadow-highlight hover:bg-surface-hover',
        outline: 'border border-border bg-transparent text-ink hover:bg-surface-alt',
        ghost: 'text-ink-secondary hover:bg-surface-alt hover:text-ink',
        destructive: 'bg-danger text-on-danger',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-9 px-3 text-subhead',
        lg: 'h-12 px-6 text-[1rem]',
        // Drawn at 40px; the invisible ::after grows the hit area to 44px
        // (ui-ux.md §9 touch targets) without changing any layout.
        icon: "relative h-10 w-10 after:absolute after:-inset-0.5 after:content-['']",
      },
    },
    defaultVariants: {
      variant: 'accent',
      size: 'default',
    },
  },
);

const MotionSlot = motion.create(Slot);

export interface ButtonProps
  extends MotionSafeProps<React.ButtonHTMLAttributes<HTMLButtonElement>>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size, className }));
    if (asChild) {
      return <MotionSlot className={classes} ref={ref} {...pressable()} {...props} />;
    }
    return <motion.button className={classes} ref={ref} {...pressable()} {...props} />;
  },
);
Button.displayName = 'Button';

/* eslint-disable-next-line react-refresh/only-export-components */
export { Button, buttonVariants };
